import { Color, InstancedMesh, Matrix4, Object3D, type BufferGeometry, type Material } from 'three';

export interface Placement {
  x: number;
  y: number;
  z: number;
  rotY?: number;
  scale?: number;
  /** Per-instance tint for parts created with `tinted: true`. */
  color?: string;
}

export interface PropPart {
  geometry: BufferGeometry;
  material: Material;
  tinted?: boolean;
}

const HIDDEN = new Matrix4().makeScale(0, 0, 0);
const helper = new Object3D();
const tint = new Color();

/**
 * One kind of prop (tree, palm, lamp…) drawn with instancing: one draw call per part for the whole street.
 * Each road tile owns a fixed block of instances and rewrites it when the tile is recycled.
 */
export class PropField {
  readonly meshes: InstancedMesh[];

  constructor(parts: readonly PropPart[], private readonly perTile: number, tiles: number) {
    const count = perTile * tiles;
    this.meshes = parts.map((part) => {
      const mesh = new InstancedMesh(part.geometry, part.material, count);
      mesh.castShadow = true;
      mesh.frustumCulled = false;
      for (let i = 0; i < count; i += 1) mesh.setMatrixAt(i, HIDDEN);
      if (part.tinted) for (let i = 0; i < count; i += 1) mesh.setColorAt(i, tint.set('#ffffff'));
      return mesh;
    });
  }

  /** Place this tile's props (world coordinates). Extra slots are hidden. */
  setTile(tileSlot: number, placements: readonly Placement[]): void {
    const start = tileSlot * this.perTile;
    for (let i = 0; i < this.perTile; i += 1) {
      const p = placements[i];
      const index = start + i;
      for (const mesh of this.meshes) {
        if (!p) {
          mesh.setMatrixAt(index, HIDDEN);
          continue;
        }
        helper.position.set(p.x, p.y, p.z);
        helper.rotation.set(0, p.rotY ?? 0, 0);
        helper.scale.setScalar(p.scale ?? 1);
        helper.updateMatrix();
        mesh.setMatrixAt(index, helper.matrix);
        if (p.color && mesh.instanceColor) mesh.setColorAt(index, tint.set(p.color));
      }
    }
    for (const mesh of this.meshes) {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
  }
}
